import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box, Divider, Grid, IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import useStyles from '../styles';

import CustomButton from '../../../../../components/General/CustomButton';
import apiClient, { GetPhenologicalStateNamesResponse } from '../../../../../requests/api/apiClient';
import { PhenologicalStateWithId } from '../../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../../types';
import { PhenologicalStateChecker } from '../../../../../utils/varieties';
import useFetchApi from '../../../../../hooks/useFetchApi';
import { CultivationCycle } from '../types';

type PhenologicalStateInstance = PhenologicalStateWithId & { phenologicalStateNameId?: string };

const UpsertPhenologicalStateModalContent = ({
  handleCloseModal,
  handleUpsertFunction,
  id,
  initialData,
  cultivationCycle,
  phenologicalStates,
}: {
  handleCloseModal: VoidFunction,
  handleUpsertFunction: (PhenologicalState: PhenologicalStateInstance) => void,
  id?: string,
  initialData?: PhenologicalStateInstance,
  cultivationCycle: CultivationCycle,
  phenologicalStates: PhenologicalStateWithId[],
}) => {
  const classes = useStyles();
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const [newPhenologicalState, setNewPhenologicalState] = useState<PhenologicalStateInstance>({
    name: initialData?.name || '',
    phenologicalStateNameId: initialData?.phenologicalStateNameId
      ? String(initialData.phenologicalStateNameId) : '',
    startDate: dayjs(initialData?.startDate).utc().format('YYYY-MM-DD') || '',
    endDate: dayjs(initialData?.endDate).utc().format('YYYY-MM-DD') || '',
    kcMin: initialData?.kcMin || undefined,
    kcMax: initialData?.kcMax || undefined,
    cycle: cultivationCycle.id,
    sector: selectedSector?.id || '',
    id: id || '',
  });

  const [errors, setErrors] = useState({
    name: '',
    startDate: '',
    endDate: '',
    kcMin: '',
    kcMax: '',
  });

  const [varietyKcValues, setVarietyKcValues] = useState<{
    kcMin?: number;
    kcMax?: number;
  }>({});

  const { data: catalogData } = useFetchApi<GetPhenologicalStateNamesResponse>({
    modelName: 'phenologicalStateName',
    modelEndpoint: 'phenologicalStateNameList',
  });
  const catalogOptions = catalogData?.phenologicalStateNames ?? [];

  useEffect(() => {
    const getVarietyKcValues = async () => {
      try {
        const varietyResponse = await apiClient.varieties.varietiesList();
        const variety = varietyResponse.data?.varieties?.find(
          (v) => v.name === cultivationCycle.varietyName
          && v.species === cultivationCycle.varietySpecies,
        );

        if (variety?.id) {
          const varietyDetailResponse = await apiClient.varieties.varietiesDetail(variety.id);
          const varietyDetail = varietyDetailResponse.data?.variety;

          if (varietyDetail?.phenologicalStates) {
            const selectedState = varietyDetail.phenologicalStates.find(
              (state) => state.name === newPhenologicalState.name,
            );

            if (selectedState) {
              setVarietyKcValues({
                kcMin: selectedState.kcMin || 0,
                kcMax: selectedState.kcMax || 0,
              });
            } else {
              setVarietyKcValues({
                kcMin: 0,
                kcMax: 0,
              });
            }
          }
        }
      } catch (error) {
        console.error('Error al obtener valores de Kc de la variedad:', error);
        setVarietyKcValues({
          kcMin: 0,
          kcMax: 0,
        });
      }
    };

    if (cultivationCycle.varietyName && cultivationCycle.varietySpecies) {
      getVarietyKcValues();
    }
  }, [cultivationCycle.varietyName, cultivationCycle.varietySpecies, newPhenologicalState.name]);

  const validateDates = (startDate: string, endDate: string) => {
    const dateErrors = {
      startDate: '',
      endDate: '',
    };

    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
      dateErrors.endDate = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }

    // Verificar superposición
    if (startDate && endDate) {
      const isEditing = !!id;
      const statesToCheck = isEditing
        ? phenologicalStates.filter((state) => state.id !== id)
        : phenologicalStates;
      const hasOverlap = PhenologicalStateChecker.isDateOverlap([
        ...statesToCheck,
        {
          ...newPhenologicalState,
          startDate,
          endDate,
          id: isEditing ? id : uuidv4(),
        },
      ]);

      if (hasOverlap) {
        dateErrors.startDate = 'Fecha tiene superposición con estados anteriores';
        dateErrors.endDate = 'Fecha tiene superposición con estados anteriores';
      }
    }

    setErrors((prev) => ({ ...prev, ...dateErrors }));
    return !dateErrors.startDate && !dateErrors.endDate;
  };

  const validateNameId = (phenologicalStateNameId: string) => {
    let nameError = '';

    if (!phenologicalStateNameId) {
      nameError = 'Debes seleccionar un estado fenológico del catálogo';
    } else {
      const existingState = phenologicalStates.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state) => (state as any).phenologicalStateNameId === phenologicalStateNameId
          && state.id !== id,
      );
      if (existingState) {
        nameError = 'Ya existe un estado fenológico con este nombre en el ciclo';
      }
    }

    setErrors((prev) => ({ ...prev, name: nameError }));
    return !nameError;
  };

  const handleDataChange = (value: string | number, name: string) => {
    const updatedState: PhenologicalStateInstance = {
      ...newPhenologicalState,
      [name]: value,
    };
    if (name === 'phenologicalStateNameId') {
      const match = catalogOptions.find((c) => c.id === value);
      updatedState.name = match?.name ?? '';
    }
    setNewPhenologicalState(updatedState);

    if (name === 'startDate' || name === 'endDate') {
      validateDates(
        name === 'startDate' ? value as string : updatedState.startDate,
        name === 'endDate' ? value as string : updatedState.endDate,
      );
    }

    if (name === 'phenologicalStateNameId') {
      validateNameId(value as string);
    }
  };

  const handleUpsertPhenologicalState = () => {
    if (newPhenologicalState.kcMin && newPhenologicalState.kcMax) {
      if (!PhenologicalStateChecker.isKcValid(
        newPhenologicalState.kcMin, newPhenologicalState.kcMax,
      )) {
        setErrors((prev) => ({ ...prev, kcMin: 'Kc mínimo debe ser menor o igual al Kc máximo' }));
        return;
      }
    }
    const isValidDates = validateDates(
      newPhenologicalState.startDate, newPhenologicalState.endDate,
    );
    const isValidName = validateNameId(newPhenologicalState.phenologicalStateNameId ?? '');

    if (!isValidDates || !isValidName) {
      return;
    }

    handleUpsertFunction({
      name: newPhenologicalState.name,
      phenologicalStateNameId: newPhenologicalState.phenologicalStateNameId,
      startDate: newPhenologicalState.startDate,
      endDate: newPhenologicalState.endDate,
      kcMin: newPhenologicalState.kcMin,
      kcMax: newPhenologicalState.kcMax,
      id: id || uuidv4(),
      cycle: cultivationCycle.id,
      sector: selectedSector?.id || '',
    });
  };

  return (
    <Box className={classes.upsertCropCycleModalContainer}>
      <Box className={classes.upsertCropCycleModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.upsertCropCycleModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            Agregar estado fenológico
          </span>
        </Typography>
        <IconButton onClick={handleCloseModal}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider className={classes.dividerStyle} />
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <TextField
            label="Nombre"
            name="phenologicalStateNameId"
            variant="standard"
            value={newPhenologicalState.phenologicalStateNameId ?? ''}
            onChange={(e) => handleDataChange(e.target.value, 'phenologicalStateNameId')}
            select
            fullWidth
            error={!!errors.name}
            helperText={errors.name}
          >
            {catalogOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item sm={6} md={6}>
          <DatePicker
            className={classes.selectField}
            label="Fecha de inicio"
            value={dayjs(newPhenologicalState.startDate).toDate()}
            onChange={(value) => value && handleDataChange(dayjs(value).format('YYYY-MM-DD'), 'startDate')}
            minDate={dayjs(cultivationCycle.startDate).toDate()}
            maxDate={dayjs(cultivationCycle.endDate).toDate()}
            slotProps={{
              textField: {
                helperText: errors.startDate,
                error: !!errors.startDate,
              },
            }}
          />
        </Grid>
        <Grid item sm={6} md={6}>
          <DatePicker
            className={classes.selectField}
            label="Fecha de fin"
            onChange={(value) => value && handleDataChange(dayjs(value).format('YYYY-MM-DD'), 'endDate')}
            minDate={dayjs(cultivationCycle.startDate).toDate()}
            maxDate={dayjs(cultivationCycle.endDate).toDate()}
            value={dayjs(newPhenologicalState.endDate).toDate()}
            slotProps={{
              textField: {
                helperText: errors.endDate,
                error: !!errors.endDate,
              },
            }}
          />
        </Grid>
        <Grid item sm={6} md={6}>
          <TextField
            label="Kc Mínimo"
            type="number"
            variant="standard"
            fullWidth
            value={newPhenologicalState.kcMin || ''}
            onChange={(e) => handleDataChange(e.target.value, 'kcMin')}
            inputProps={{
              step: 0.1,
              min: 0,
            }}
            error={!!errors.kcMin}
            helperText={errors.kcMin || 'Coeficiente de cultivo mínimo'}
            placeholder={varietyKcValues.kcMin?.toString() || '0'}
          />
        </Grid>
        <Grid item sm={6} md={6}>
          <TextField
            label="Kc Máximo"
            type="number"
            variant="standard"
            fullWidth
            value={newPhenologicalState.kcMax || ''}
            onChange={(e) => handleDataChange(e.target.value, 'kcMax')}
            inputProps={{
              step: 0.1,
              min: 0,
            }}
            error={!!errors.kcMax}
            helperText={errors.kcMax || 'Coeficiente de cultivo máximo'}
            placeholder={varietyKcValues.kcMax?.toString() || '0'}
          />
        </Grid>
      </Grid>
      <Box className={classes.upsertCropCycleModalButtonsContainer}>
        <Box width='100%' mr={2}><CustomButton color='blue' buttonText='Cancelar' onClick={handleCloseModal} /></Box>
        <Box width='100%' ml={2}><CustomButton color='green' buttonText={id ? 'Editar' : 'Agregar'} onClick={handleUpsertPhenologicalState} /></Box>
      </Box>
    </Box>
  );
};

export default UpsertPhenologicalStateModalContent;
